import React from 'react';

interface ChatButtonProps {
  onClick: () => void;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  size?: number;
  primaryColor?: string;
  ariaLabel?: string;
  preset?: 'classic' | 'nv' | 'nevi';
  assetBaseUrl?: string;
}

/**
 * Chat button component for opening the minimized chat widget
 */
export const ChatButton: React.FC<ChatButtonProps> = ({
  onClick,
  position = 'bottom-right',
  size = 60,
  primaryColor = '#8b5cf6',
  ariaLabel = 'Open chat',
  preset = 'classic',
  assetBaseUrl = '',
}) => {
  const isNv = preset === 'nv';
  const isNevi = preset === 'nevi';
  // Position classes mapping
  const positionClasses = {
    'bottom-right': 'wc-bottom-4 wc-right-4',
    'bottom-left': 'wc-bottom-4 wc-left-4',
    'top-right': 'wc-top-4 wc-right-4',
    'top-left': 'wc-top-4 wc-left-4',
  };

  if (isNevi) {
    // NeVi: dark pill with the mascot avatar — Figma node 1906:18731
    return (
      <button
        onClick={onClick}
        className={`wc-group wc-fixed wc-z-[9998] wc-flex wc-items-center wc-justify-center wc-rounded-full wc-cursor-pointer wc-chat-button ${positionClasses[position]}`}
        style={{
          width: '72px',
          height: '72px',
          background: '#2B323B',
          border: '1px solid rgba(251, 251, 251, 0.15)',
          padding: '4px',
          boxShadow: '-12px -16px 32px 4px rgba(0, 0, 0, 0.2)',
          animation: 'buttonEntrance 0.3s ease-out',
          transition: 'opacity 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.85';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
        aria-label={ariaLabel}
      >
        <img
          src={`${assetBaseUrl}/widget/nevi-avatar.webp`}
          alt=""
          style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover' }}
        />
      </button>
    );
  }

  if (isNv) {
    const nvSize = 56;
    return (
      <button
        onClick={onClick}
        className={`wc-group wc-fixed wc-z-[9998] wc-flex wc-items-center wc-justify-center wc-rounded-full wc-cursor-pointer wc-chat-button ${positionClasses[position]}`}
        style={{
          width: `${nvSize}px`,
          height: `${nvSize}px`,
          background: 'linear-gradient(104.04deg, #FE7338 0%, #EA3700 100%)',
          border: 'none',
          padding: '12px',
          animation: 'buttonEntrance 0.3s ease-out',
          transition: 'opacity 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.8';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
        aria-label={ariaLabel}
      >
        <img src={`${assetBaseUrl}/widget/chat-icon-nv.svg`} alt="" style={{ width: '24px', height: '24px' }} />
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`wc-group wc-fixed wc-z-[9998] wc-flex wc-items-center wc-justify-center wc-rounded-full wc-cursor-pointer wc-chat-button ${positionClasses[position]}`}
      style={
        {
          width: `${size}px`,
          height: `${size}px`,
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, -20)} 100%)`,
          border: 'none',
          animation: 'buttonEntrance 0.3s ease-out forwards',
          '--primary-color': primaryColor,
          transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease',
        } as React.CSSProperties
      }
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
      }}
      aria-label={ariaLabel}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="white"
        style={{
          width: `${size * 0.5}px`,
          height: `${size * 0.5}px`,
          transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
        }}
        className="wc-chat-icon"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
        />
      </svg>
    </button>
  );
};

/**
 * Adjust color brightness
 */
function adjustColor(color: string, amount: number): string {
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
